import axios from "axios";
import { CONFIG } from "../config";
import { setupAuthInterceptors } from "../utils/auth";

const api = setupAuthInterceptors(
  axios.create({
    baseURL: CONFIG.BACKEND_URL,
    headers: {
      "Content-Type": "application/json",
    },
  }),
);

/**
 * @typedef {Object} TaskHistoryItem
 * @property {string} id - Task ID
 * @property {string} robot_name - 로봇 이름
 * @property {string} created_at - 생성 시간
 * @property {string} command - 명령어
 * @property {string} poi_id - POI ID
 * @property {string} user_id - 사용자 ID
 * @property {string} status - 상태 (pending/success/failed)
 */

/**
 * @typedef {Object} TaskHistoryResponse
 * @property {TaskHistoryItem[]} data - 작업 이력 목록
 * @property {{ page: number, limit: number, total: number }} pagination - 페이지네이션 정보
 */

/**
 * Get task history
 * @param {Object} params - Query parameters
 * @param {string} [params.deviceId] - Device ID
 * @param {string} [params.startDate] - Start date (YYYY-MM-DD)
 * @param {string} [params.endDate] - End date (YYYY-MM-DD)
 * @param {string} [params.userId] - User ID
 * @param {string} [params.status] - Task status (pending/success/failed)
 * @param {number} [params.page] - Page number (default: 1)
 * @param {number} [params.limit] - Items per page (default: 20)
 * @returns {Promise<TaskHistoryResponse>} Task history with pagination (interceptor에서 언래핑)
 */
export const getTaskHistory = async (params) => {
  const {
    deviceId,
    startDate,
    endDate,
    userId,
    status,
    page = 1,
    limit = 10,
  } = params;
  const queryParams = new URLSearchParams();

  if (deviceId) queryParams.append("deviceId", deviceId);
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);
  if (userId) queryParams.append("userId", userId);
  if (status) queryParams.append("status", status);
  if (page) queryParams.append("page", page);
  if (limit) queryParams.append("limit", limit);

  const response = await api.get(`/history/tasks?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get fault history
 * @param {Object} params - Query parameters
 * @param {string} [params.deviceId] - Device ID
 * @param {string} [params.startDate] - Start date (KST, YYYY-MM-DD)
 * @param {string} [params.endDate] - End date (KST, YYYY-MM-DD)
 * @param {number} [params.page] - Page number (default: 1)
 * @param {number} [params.limit] - Items per page (default: 20)
 * @returns {Promise<{content: Array<{robot_name: string, device_id: string, created_at: string, errorCode: string, errorMessage: string, cause: string, actionMethod: string}>, page: number, size: number, totalElements: number}>} Fault history with pagination (interceptor에서 언래핑)
 */
export const getFaultHistory = async (params) => {
  const { deviceId, startDate, endDate, page = 1, limit = 10 } = params;
  const queryParams = new URLSearchParams();

  if (deviceId) queryParams.append("deviceId", deviceId);
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);
  queryParams.append("page", page);
  queryParams.append("limit", limit);

  const response = await api.get(`/history/faults?${queryParams.toString()}`);
  return response.data;
};

/**
 * Get login history
 * @param {Object} params - Query parameters
 * @param {string} [params.deviceId] - Device ID
 * @param {string} [params.startDate] - Start date (KST, YYYY-MM-DD)
 * @param {string} [params.endDate] - End date (KST, YYYY-MM-DD)
 * @param {number} [params.page] - Page number (default: 1)
 * @param {number} [params.limit] - Items per page (default: 20)
 * @returns {Promise<Object>} Login history with pagination (interceptor에서 언래핑)
 */
export const getLoginHistory = async (params) => {
  const { deviceId, startDate, endDate, page = 1, limit = 10 } = params;
  const queryParams = new URLSearchParams();

  if (deviceId) queryParams.append("deviceId", deviceId);
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);
  queryParams.append("page", page);
  queryParams.append("limit", limit);

  const response = await api.get(`/history/logins?${queryParams.toString()}`);
  console.log("response", response);
  return response.data;
};

const downloadCsv = (endpoint, { deviceId, startDate, endDate }) => {
  const queryParams = new URLSearchParams();
  if (deviceId) queryParams.append("deviceId", deviceId);
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);
  return api.get(`${endpoint}?${queryParams.toString()}`, { responseType: "blob" });
};

export const downloadTaskHistoryCsv = (params) => downloadCsv("/history/tasks/csv", params);
export const downloadFaultHistoryCsv = (params) => downloadCsv("/history/faults/csv", params);
export const downloadLoginHistoryCsv = (params) => downloadCsv("/history/logins/csv", params);

const historyApi = {
  getTaskHistory,
  getFaultHistory,
  getLoginHistory,
  downloadTaskHistoryCsv,
  downloadFaultHistoryCsv,
  downloadLoginHistoryCsv,
};

export default historyApi;
